import LogoutPage from "@/pages/LogoutPage.vue";
import HomePage from "@/pages/authRequired/HomePage.vue";
import SafePiPlanningsPage from "@/pages/authRequired/SafePiPlanningsPage.vue";
import SettingsPage from "@/pages/authRequired/SettingsPage.vue";
import TrainingBillingPage from "@/pages/authRequired/TrainingBillingPage.vue";
import UnauthorizedPage from "@/pages/authRequired/additionalPages/UnauthorizedPage.vue";
import AddAlmConnection from "@/pages/authRequired/almConnections/AddAlmConnection.vue";
import AlmConnectionsPage from "@/pages/authRequired/almConnections/AlmConnectionsPage.vue";
import ProfilePage from "@/pages/authRequired/users/ProfilePage.vue";
import { lazyRoute } from "@/utils/load";

import { getPublicPagesRoutes } from "./publicPages";

const AddNewSafePiPlanningPage = lazyRoute(
  () => import("@/pages/authRequired/AddNewSafePiPlanningPage.vue"),
);
const editSession = lazyRoute(() => import("@/pages/authRequired/sessions/EditSession.vue"));
const SelectIdentityProvider = lazyRoute(
  () => import("@/pages/authRequired/idp/SelectIdentityProvider.vue"),
);
const IdentityProvider = lazyRoute(() => import("@/pages/authRequired/idp/IdentityProvider.vue"));
const SetupOIDC = lazyRoute(() => import("@/pages/authRequired/idp/SetupOIDC.vue"));
const SetupSAMLv2 = lazyRoute(() => import("@/pages/authRequired/idp/SetupSAMLv2.vue"));
const EditTeam = lazyRoute(() => import("@/pages/authRequired/organization/EditTeam.vue"));
const EditUser = lazyRoute(() => import("@/pages/authRequired/users/EditUser.vue"));
const TeamsList = lazyRoute(() => import("@/pages/authRequired/organization/TeamsList.vue"));
const UsersPage = lazyRoute(() => import("@/pages/authRequired/users/UsersPage.vue"));
const BillingPage = lazyRoute(() => import("@/pages/authRequired/BillingPage.vue"));
const SelectOptions = lazyRoute(() => import("@/pages/authRequired/billing/SelectOptions.vue"));
const ExtendLicenses = lazyRoute(() => import("@/pages/authRequired/billing/ExtendLicenses.vue"));
const BuyLicenses = lazyRoute(() => import("@/pages/authRequired/billing/BuyLicenses.vue"));
const UpgradeLicenses = lazyRoute(() => import("@/pages/authRequired/billing/UpgradeLicenses.vue"));
const Invoice = lazyRoute(() => import("@/pages/authRequired/billing/InvoiceLicenses.vue"));
const EditAlmConnection = lazyRoute(
  () => import("@/pages/authRequired/almConnections/EditAlmConnection.vue"),
);
const AlmConnection = lazyRoute(
  () => import("@/pages/authRequired/almConnections/AlmConnection.vue"),
);
const AlmWebhook = lazyRoute(() => import("@/pages/authRequired/almConnections/AlmWebhook.vue"));
const AlmTeamMapping = lazyRoute(
  () => import("@/pages/authRequired/almConnections/AlmTeamMapping.vue"),
);
const AlmUserMapping = lazyRoute(
  () => import("@/pages/authRequired/almConnections/AlmUserMapping.vue"),
);
const acWorkspace = lazyRoute(() => import("@/components/almToolConfig/ac/AcWorkspace.vue"));
const adoProject = lazyRoute(() => import("@/components/almToolConfig/ado/AdoProject.vue"));
const AlmTeam = lazyRoute(() => import("@/pages/authRequired/almConnections/AlmTeam.vue"));
const AlmArtMapping = lazyRoute(
  () => import("@/pages/authRequired/almConnections/AlmArtMapping.vue"),
);
const AlmArt = lazyRoute(() => import("@/pages/authRequired/almConnections/AlmArt.vue"));
const AlmEvents = lazyRoute(() => import("@/pages/authRequired/almConnections/AlmEventsPage.vue"));
const OrganizationPage = lazyRoute(
  () => import("@/pages/authRequired/organization/OrganizationPage.vue"),
);
const ArtsList = lazyRoute(() => import("@/pages/authRequired/organization/ArtsList.vue"));
const EditArt = lazyRoute(() => import("@/pages/authRequired/organization/EditArt.vue"));
const SolutionTrainsList = lazyRoute(
  () => import("@/pages/authRequired/organization/SolutionTrainsList.vue"),
);
const EditSolutionTrain = lazyRoute(
  () => import("@/pages/authRequired/organization/EditSolutionTrain.vue"),
);
const ExternalOrgList = lazyRoute(
  () => import("@/pages/authRequired/organization/ExternalOrgList.vue"),
);
const EditExternalOrg = lazyRoute(
  () => import("@/pages/authRequired/organization/EditExternalOrg.vue"),
);
const OrganizationTreePage = lazyRoute(
  () => import("@/pages/authRequired/organization/OrganizationTreePage.vue"),
);

const HelpPage = lazyRoute(() => import("@/pages/authRequired/HelpPage.vue"));

export default {
  scrollBehavior() {
    // Body scroll is disabled — `.layout__content` owns the viewport scroll.
    // Reset its scroll position manually on route change.
    return new Promise((resolve) => {
      setTimeout(() => {
        document.querySelector(".layout__content")?.scrollTo({ left: 0, top: 0 });
        resolve();
      }, 10);
    });
  },
  routes: [
    ...getPublicPagesRoutes(),
    {
      path: "/",
      name: "home",
      component: HomePage,
      meta: {
        roles: ["admin"],
        default: 0,
        title: "Home",
      },
    },
    {
      path: "/billing",
      component: BillingPage,
      meta: {
        roles: ["admin"],
        isCloud: true,
        training: false,
        title: "Billing",
      },
      children: [
        {
          path: "",
          name: "billing",
          component: SelectOptions,
          meta: {
            roles: ["admin"],
            isCloud: true,
            training: false,
            title: "Billing",
          },
        },
        {
          path: "extend",
          component: ExtendLicenses,
          meta: {
            roles: ["admin"],
            isCloud: true,
            training: false,
            title: "Billing, Extend licenses",
          },
        },
        {
          path: "buyMore",
          component: BuyLicenses,
          meta: {
            roles: ["admin"],
            isCloud: true,
            training: false,
            title: "Billing, Buy more licenses",
          },
        },
        {
          path: "upgrade",
          component: UpgradeLicenses,
          meta: {
            roles: ["admin"],
            isCloud: true,
            training: false,
            title: "Billing, Upgrade plan",
          },
        },
        {
          path: "invoice",
          component: Invoice,
          meta: {
            roles: ["admin"],
            isCloud: true,
            training: false,
            title: "Billing, Pay by invoice",
          },
        },
      ],
    },
    {
      path: "/trainingbilling",
      name: "trainingBilling",
      component: TrainingBillingPage,
      meta: {
        roles: ["admin"],
        isCloud: true,
        training: true,
        title: "Training Billing",
      },
    },
    {
      path: "/learn",
      name: "learn",
      beforeEnter: () => {
        window.open("https://www.piplanning.io/learn");
      },
    },
    {
      path: "/users",
      name: "users",
      component: UsersPage,
      meta: { roles: ["admin"], onPrem: true, default: 2, title: "Users" },
    },
    {
      path: "/users/new",
      name: "createUser",
      component: EditUser,
      props: { title: "Create User" },
      meta: {
        roles: ["admin"],
        onPrem: true,
        backLink: { name: "users" },
        title: "Create User",
      },
    },
    {
      path: "/users/:id",
      name: "editUser",
      component: EditUser,
      props: (route) => ({ id: route.params.id, title: "Edit User" }),
      meta: {
        roles: ["admin"],
        onPrem: true,
        title: "Edit User",
        backLink: { name: "users" },
      },
    },
    {
      path: "/organization",
      name: "organization",
      component: OrganizationPage,
      meta: { roles: ["admin", "planning_interval_admin"] },
      // The overview orients you before the entity lists, and heads the sidebar
      redirect: { name: "organization_tree" },
      children: [
        {
          path: "teams",
          name: "teams",
          component: TeamsList,
          meta: { roles: ["admin", "planning_interval_admin"], title: "Teams" },
        },
        {
          path: "teams/:id",
          name: "editTeam",
          component: EditTeam,
          props: (route) => ({ id: route.params.id }),
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Edit Team",
            backLink: { name: "teams" },
          },
        },
        {
          path: "teams/new",
          name: "createTeam",
          component: EditTeam,
          meta: {
            roles: ["admin"],
            title: "Create Team",
            backLink: { name: "teams" },
          },
        },
        {
          path: "arts",
          name: "arts",
          component: ArtsList,
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Agile Release Trains",
          },
        },
        {
          path: "arts/new",
          name: "createArt",
          component: EditArt,
          meta: {
            roles: ["admin"],
            title: "Create ART",
            backLink: { name: "arts" },
          },
        },
        {
          path: "arts/:id",
          name: "editArt",
          component: EditArt,
          props: (route) => ({ artId: route.params.id }),
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Edit ART",
            backLink: { name: "arts" },
          },
        },
        {
          path: "solution_trains",
          name: "solution_trains",
          component: SolutionTrainsList,
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Solution Trains",
          },
        },
        {
          path: "solution_trains/new",
          name: "createSolutionTrain",
          component: EditSolutionTrain,
          meta: {
            roles: ["admin"],
            title: "Create Solution Train",
            backLink: { name: "solution_trains" },
          },
        },
        {
          path: "solution_trains/:id",
          name: "editSolutionTrain",
          component: EditSolutionTrain,
          props: (route) => ({ solutionTrainId: route.params.id }),
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Edit Solution Train",
            backLink: { name: "solution_trains" },
          },
        },
        {
          path: "tree",
          name: "organization_tree",
          component: OrganizationTreePage,
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Organization Overview",
          },
        },
        {
          path: "external_orgs",
          name: "external_orgs",
          component: ExternalOrgList,
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "External Orgs",
          },
        },
        {
          path: "external_orgs/new",
          name: "createExternalOrg",
          component: EditExternalOrg,
          meta: {
            roles: ["admin"],
            title: "Create External Org",
            backLink: { name: "external_orgs" },
          },
        },
        {
          path: "external_orgs/:id",
          name: "editExternalOrg",
          component: EditExternalOrg,
          props: (route) => ({ externalOrgId: route.params.id }),
          meta: {
            roles: ["admin", "planning_interval_admin"],
            title: "Edit External Org",
            backLink: { name: "external_orgs" },
          },
        },
      ],
    },
    {
      path: "/safepiplannings",
      name: "safepiplannings",
      component: SafePiPlanningsPage,
      meta: {
        roles: ["admin", "planning_interval_admin"],
        default: 1,
        title: "PI Sessions",
      },
    },
    {
      path: "/add-safepiplanning",
      name: "addnewsafepiplanning",
      component: AddNewSafePiPlanningPage,
      meta: {
        roles: ["admin", "planning_interval_admin"],
        title: "Add new PI Session",
      },
    },
    {
      path: "/edit-session",
      name: "editSession",
      component: editSession,
      meta: {
        roles: ["admin", "planning_interval_admin"],
        title: "Edit PI",
      },
      // Permission check is in router/index.js (editSession guard) where we have pinia access
    },
    {
      path: "/alm-connections",
      name: "almConnections",
      component: AlmConnectionsPage,
      meta: { roles: ["admin"], title: "ALM Connections" },
    },
    {
      path: "/alm-connections/new",
      name: "AddAlmConnection",
      component: AddAlmConnection,
      meta: { roles: ["admin"], title: "Add ALM Connection" },
    },
    {
      path: "/alm-connections/:almType/:connectionId",
      component: EditAlmConnection,
      props: true,
      meta: { roles: ["admin"], title: "Edit ALM Connection" },
      children: [
        {
          path: "",
          name: "editAlmConnection",
          component: AlmConnection,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM Connection" },
        },
        {
          path: "webhook",
          name: "editAlmWebhook",
          component: AlmWebhook,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM Webhook" },
        },
        {
          path: "teams",
          name: "editAlmTeamMapping",
          component: AlmTeamMapping,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM Team Mapping" },
        },
        {
          path: "teams/:teamId",
          name: "editAlmTeam",
          component: AlmTeam,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM Team" },
        },
        {
          path: "arts",
          name: "editAlmArtMapping",
          component: AlmArtMapping,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM ART Mapping" },
        },
        {
          path: "arts/:artId",
          name: "editAlmArt",
          component: AlmArt,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM ART" },
        },
        {
          path: "workspace",
          name: "editAlmWorkspace",
          component: acWorkspace,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM Workspace" },
        },
        {
          path: "project",
          name: "editAlmProject",
          component: adoProject,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM Project" },
        },
        {
          path: "user-mapping",
          name: "editAlmUserMapping",
          component: AlmUserMapping,
          props: true,
          meta: { roles: ["admin"], title: "Edit ALM User Mapping" },
        },
        {
          path: "events/:eventId?",
          name: "almConnectionEvents",
          component: AlmEvents,
          props: true,
          meta: { roles: ["admin"], title: "ALM Connection Events" },
        },
      ],
    },
    {
      path: "/idp",
      component: IdentityProvider,
      meta: {
        roles: ["admin"],
        onPrem: true,
        isSSO: true,
        title: "Identity Provider",
      },
      children: [
        {
          path: "",
          name: "SelectIdentityProvider",
          component: SelectIdentityProvider,
          meta: {
            roles: ["admin"],
            onPrem: true,
            isSSO: true,
            title: "Select Identity Provider",
          },
        },
        {
          path: "saml",
          name: "SetupSAMLv2",
          component: SetupSAMLv2,
          meta: {
            roles: ["admin"],
            onPrem: true,
            isSSO: true,
            title: "Setup SAML v2",
          },
        },
        {
          path: "oidc",
          name: "SetupOIDC",
          component: SetupOIDC,
          meta: {
            roles: ["admin"],
            onPrem: true,
            isSSO: true,
            title: "Setup OIDC",
          },
        },
      ],
    },
    {
      path: "/settings",
      name: "settings",
      component: SettingsPage,
      meta: { roles: ["admin"], title: "Settings" },
    },
    {
      path: "/help",
      name: "help",
      component: HelpPage,
      meta: {
        roles: ["user", "admin", "planning_interval_admin", "observer"],
        onPrem: true,
        title: "Help",
      },
    },
    {
      path: "/profile",
      name: "profile",
      component: ProfilePage,
      meta: {
        roles: ["user", "admin", "planning_interval_admin", "observer"],
        default: 3,
        title: "Profile",
      },
    },
    {
      path: "/logout",
      name: "logout",
      component: LogoutPage,
    },
    {
      path: "/403",
      name: "403",
      component: UnauthorizedPage,
      meta: { hideMenu: true, title: "ERROR: 403 Forbidden" },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: { name: "home" },
    },
  ],
};
